<script setup>
// Add to blacklist dialog
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const emit = defineEmits(['close', 'submit']);
const { t } = useI18n();

const dialogRef = ref(null);

const phoneNumber = ref('');
const reason = ref('manual');
const notes = ref('');
const expiresAt = ref('');

const reasonOptions = computed(() => [
  { value: 'manual', label: t('CAMPAIGN.BLACKLIST.REASONS.MANUAL') },
  { value: 'optout', label: t('CAMPAIGN.BLACKLIST.REASONS.OPTOUT') },
  { value: 'bounce', label: t('CAMPAIGN.BLACKLIST.REASONS.BOUNCE') },
  { value: 'complaint', label: t('CAMPAIGN.BLACKLIST.REASONS.COMPLAINT') },
  { value: 'spam', label: t('CAMPAIGN.BLACKLIST.REASONS.SPAM') },
]);

const isValid = computed(() => {
  return phoneNumber.value.replace(/\D/g, '').length >= 10;
});

const resetForm = () => {
  phoneNumber.value = '';
  reason.value = 'manual';
  notes.value = '';
  expiresAt.value = '';
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleSubmit = () => {
  if (!isValid.value) return;

  emit('submit', {
    phone_number: phoneNumber.value,
    reason: reason.value,
    notes: notes.value,
    expires_at: expiresAt.value || null,
  });

  close();
};

const handleClose = () => {
  emit('close');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="$t('CAMPAIGN.BLACKLIST.ADD')"
    width="lg"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleClose"
  >
    <form class="space-y-4" @submit.prevent="handleSubmit">
      <Input
        v-model="phoneNumber"
        :label="$t('CAMPAIGN.BLACKLIST.FORM.PHONE')"
        placeholder="+5511999999999"
        type="tel"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('CAMPAIGN.BLACKLIST.TABLE.REASON') }}
        </label>
        <ComboBox
          v-model="reason"
          :options="reasonOptions"
          :placeholder="$t('CAMPAIGN.BLACKLIST.FORM.REASON_PLACEHOLDER')"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <TextArea
        v-model="notes"
        :label="$t('CAMPAIGN.BLACKLIST.FORM.NOTES')"
        :placeholder="$t('CAMPAIGN.BLACKLIST.FORM.NOTES_PLACEHOLDER')"
        rows="3"
      />

      <Input
        v-model="expiresAt"
        :label="$t('CAMPAIGN.BLACKLIST.FORM.EXPIRES')"
        type="datetime-local"
      />
      <p class="text-xs text-n-slate-11 -mt-2">
        {{ $t('CAMPAIGN.BLACKLIST.FORM.PERMANENT_HINT') }}
      </p>

      <div class="flex justify-end gap-3 pt-4 border-t border-n-weak">
        <Button type="button" variant="faded" color="slate" @click="close">
          {{ $t('CAMPAIGN.BLACKLIST.FORM.CANCEL') }}
        </Button>
        <Button type="submit" :disabled="!isValid">
          {{ $t('CAMPAIGN.BLACKLIST.FORM.SUBMIT') }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>
